import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormFieldCombobox } from '~/components/FormField/components/FormFieldCombobox';
import { FormFieldInput } from '~/components/FormField/components/FormFieldInput';
import { FormFieldRadioGroup } from '~/components/FormField/components/FormFieldRadioGroup';
import { FormFieldToggle } from '~/components/FormField/components/FormFieldToggle';
import { LOCALES } from '~/i18n/locales';
import { useUser } from '~/modules/auth/hooks/useUser';
import { ContentBox } from '~/modules/my-profile/components/ContentBox';

import { type MyProfileFormSchema, myProfileFormSchema } from './schema';

const defaultValues: MyProfileFormSchema = {
  phoneNumber: '',
  language: { value: 'en-US', label: 'English' },
  dateFormat: 'MM-DD-YYYY',
  digitalReleaseNotifications: {
    importDraftUploaded: false,
    failureDmdCreation: false,
    failureGcdmCreation: false,
    releaseSubmitted: false,
    releaseRetracted: false,
    releaseDbom: false,
    userRequestedCancelation: false,
    releaseRequestedEditing: false,
    userRequestedTakedown: false,
    releaseTakenDown: false,
    releaseRejected: false,
    releaseApprovedByAda: false,
    releaseApproved: false,
    releaseClaimed: false,
    releaseClaimingFailed: false,
  },
};

import { AccordionContentBox } from '~/components/AccordionContentBox';
import { ColumnLayout } from '~/components/ColumnLayout';
import { Form } from '~/components/Form';
import { Layout } from '~/components/Layout';

import { UserDetails } from '../../components/UserDetails';

export function MyProfilePage() {
  const user = useUser();
  const { t } = useTranslation('my-profile');

  const form = useForm<MyProfileFormSchema>({
    defaultValues,
    resolver: zodResolver(myProfileFormSchema),
    mode: 'onBlur',
  });

  const handleSubmit = (data: MyProfileFormSchema) => {
    // eslint-disable-next-line no-console
    console.log('Form submitted:', data);
  };

  const handleSelectAllDigital = () => {
    const allNotifications = Object.keys(
      defaultValues.digitalReleaseNotifications,
    ).reduce(
      (acc, key) => ({
        ...acc,
        [key]: true,
      }),
      {} as MyProfileFormSchema['digitalReleaseNotifications'],
    );

    form.setValue('digitalReleaseNotifications', allNotifications);
  };

  const handleClearAllDigital = () => {
    const allNotifications = Object.keys(
      defaultValues.digitalReleaseNotifications,
    ).reduce(
      (acc, key) => ({
        ...acc,
        [key]: false,
      }),
      {} as MyProfileFormSchema['digitalReleaseNotifications'],
    );

    form.setValue('digitalReleaseNotifications', allNotifications);
  };

  const localeToTranslationKey: Record<(typeof LOCALES)[number], string> = {
    'en-US': 'english',
    es: 'spanish',
    fr: 'french',
    'ja-JP': 'japanese',
    'ko-KR': 'korean',
    'pt-BR': 'portuguese',
    'zh-CN': 'chinese',
    ar: 'arabic',
  };

  const languageOptions = LOCALES.map((locale) => {
    const translationKey = localeToTranslationKey[locale];

    return {
      label: translationKey ? t(`languages.${translationKey}`) : locale,
      value: locale,
    };
  });

  const dateFormatOptions = [
    { label: t('dateFormats.mmDdYyyy'), value: 'MM-DD-YYYY' },
    { label: t('dateFormats.ddMmYyyy'), value: 'DD-MM-YYYY' },
    { label: t('dateFormats.ddMmmYyyy'), value: 'DD-MMM-YYYY' },
  ];

  // TODO: get from the API
  const roles = ['Role 1', 'Role 2', 'Role 3'];
  const labelGroups = ['Label Group 1', 'Label Group 2'];

  return (
    <Layout>
      <div className="pb-xl">
        <UserDetails user={user} />
      </div>
      <Form
        formHook={form}
        onSubmit={handleSubmit}
        schema={myProfileFormSchema}
      >
        <div className="space-y-xl">
          <AccordionContentBox title={t('basicInformation.title')} defaultOpen>
            <ColumnLayout columns={3}>
              <FormFieldInput
                name="phoneNumber"
                label={t('basicInformation.phoneNumber')}
                placeholder={t('basicInformation.phoneNumberPlaceholder')}
              />
              <FormFieldCombobox
                name="language"
                label={t('basicInformation.language')}
                options={languageOptions}
                placeholder={t('basicInformation.languageSelectPlaceholder')}
              />
              <FormFieldRadioGroup
                name="dateFormat"
                label={t('basicInformation.dateFormat')}
                options={dateFormatOptions}
                variant="radio"
              />
            </ColumnLayout>
          </AccordionContentBox>

          <AccordionContentBox
            title={t('notifications.title')}
            defaultOpen
            contentClassName="space-y-lg"
          >
            <AccordionContentBox
              title={t('notifications.digitalRelease.title')}
              defaultOpen
              variant={{ level: 2 }}
            >
              <div className="flex justify-end gap-md pb-lg text-body-sm">
                <button
                  type="button"
                  className="text-strong"
                  onClick={handleSelectAllDigital}
                >
                  {t('notifications.selectAll')}
                </button>
                <button
                  type="button"
                  className="text-strong"
                  onClick={handleClearAllDigital}
                >
                  {t('notifications.clearAll')}
                </button>
              </div>
              <ColumnLayout columns={2}>
                {/* Left Column */}
                <div className="flex flex-col gap-md">
                  {/* Creation and upload */}
                  <ContentBox>
                    <h3 className="mb-lg text-headline-sm font-semibold">
                      {t(
                        'notifications.digitalRelease.creationAndUpload.title',
                      )}
                    </h3>
                    <div className="flex flex-col gap-md">
                      <FormFieldToggle
                        name="digitalReleaseNotifications.importDraftUploaded"
                        label={t(
                          'notifications.digitalRelease.creationAndUpload.importDraftUploaded',
                        )}
                        layout="horizontal"
                        activeLabel={t('notifications.emailLabel')}
                        inactiveLabel={t('notifications.emailLabel')}
                      />
                      <FormFieldToggle
                        name="digitalReleaseNotifications.failureDmdCreation"
                        label={t(
                          'notifications.digitalRelease.creationAndUpload.failureDmdCreation',
                        )}
                        layout="horizontal"
                        activeLabel={t('notifications.emailLabel')}
                        inactiveLabel={t('notifications.emailLabel')}
                      />
                      <FormFieldToggle
                        name="digitalReleaseNotifications.failureGcdmCreation"
                        label={t(
                          'notifications.digitalRelease.creationAndUpload.failureGcdmCreation',
                        )}
                        layout="horizontal"
                        activeLabel={t('notifications.emailLabel')}
                        inactiveLabel={t('notifications.emailLabel')}
                      />
                    </div>
                  </ContentBox>

                  {/* Submission and packaging */}
                  <ContentBox>
                    <h3 className="mb-lg text-headline-sm font-semibold">
                      {t(
                        'notifications.digitalRelease.submissionAndPackaging.title',
                      )}
                    </h3>
                    <div className="flex flex-col gap-md">
                      <FormFieldToggle
                        name="digitalReleaseNotifications.releaseSubmitted"
                        label={t(
                          'notifications.digitalRelease.submissionAndPackaging.releaseSubmitted',
                        )}
                        layout="horizontal"
                        activeLabel={t('notifications.emailLabel')}
                        inactiveLabel={t('notifications.emailLabel')}
                      />
                      <FormFieldToggle
                        name="digitalReleaseNotifications.releaseRetracted"
                        label={t(
                          'notifications.digitalRelease.submissionAndPackaging.releaseRetracted',
                        )}
                        layout="horizontal"
                        activeLabel={t('notifications.emailLabel')}
                        inactiveLabel={t('notifications.emailLabel')}
                      />
                      <FormFieldToggle
                        name="digitalReleaseNotifications.releaseDbom"
                        label={t(
                          'notifications.digitalRelease.submissionAndPackaging.releaseDbom',
                        )}
                        layout="horizontal"
                        activeLabel={t('notifications.emailLabel')}
                        inactiveLabel={t('notifications.emailLabel')}
                      />
                    </div>
                  </ContentBox>

                  {/* Editing and cancellation */}
                  <ContentBox>
                    <h3 className="mb-lg text-headline-sm font-semibold">
                      {t(
                        'notifications.digitalRelease.editingAndCancellation.title',
                      )}
                    </h3>
                    <div className="flex flex-col gap-md">
                      <FormFieldToggle
                        name="digitalReleaseNotifications.userRequestedCancelation"
                        label={t(
                          'notifications.digitalRelease.editingAndCancellation.userRequestedCancelation',
                        )}
                        layout="horizontal"
                        activeLabel={t('notifications.emailLabel')}
                        inactiveLabel={t('notifications.emailLabel')}
                      />
                      <FormFieldToggle
                        name="digitalReleaseNotifications.releaseRequestedEditing"
                        label={t(
                          'notifications.digitalRelease.editingAndCancellation.releaseRequestedEditing',
                        )}
                        layout="horizontal"
                        activeLabel={t('notifications.emailLabel')}
                        inactiveLabel={t('notifications.emailLabel')}
                      />
                    </div>
                  </ContentBox>
                </div>

                {/* Right Column */}
                <div className="flex flex-col gap-md">
                  {/* Rejection and removal */}
                  <ContentBox>
                    <h3 className="mb-lg text-headline-sm font-semibold">
                      {t(
                        'notifications.digitalRelease.rejectionAndRemoval.title',
                      )}
                    </h3>
                    <div className="flex flex-col gap-md">
                      <FormFieldToggle
                        name="digitalReleaseNotifications.userRequestedTakedown"
                        label={t(
                          'notifications.digitalRelease.rejectionAndRemoval.userRequestedTakedown',
                        )}
                        layout="horizontal"
                        activeLabel={t('notifications.emailLabel')}
                        inactiveLabel={t('notifications.emailLabel')}
                      />
                      <FormFieldToggle
                        name="digitalReleaseNotifications.releaseTakenDown"
                        label={t(
                          'notifications.digitalRelease.rejectionAndRemoval.releaseTakenDown',
                        )}
                        layout="horizontal"
                        activeLabel={t('notifications.emailLabel')}
                        inactiveLabel={t('notifications.emailLabel')}
                      />
                      <FormFieldToggle
                        name="digitalReleaseNotifications.releaseRejected"
                        label={t(
                          'notifications.digitalRelease.rejectionAndRemoval.releaseRejected',
                        )}
                        layout="horizontal"
                        activeLabel={t('notifications.emailLabel')}
                        inactiveLabel={t('notifications.emailLabel')}
                      />
                    </div>
                  </ContentBox>

                  {/* Approval */}
                  <ContentBox>
                    <h3 className="mb-lg text-headline-sm font-semibold">
                      {t('notifications.digitalRelease.approval.title')}
                    </h3>
                    <div className="flex flex-col gap-md">
                      <FormFieldToggle
                        name="digitalReleaseNotifications.releaseApprovedByAda"
                        label={t(
                          'notifications.digitalRelease.approval.releaseApprovedByAda',
                        )}
                        layout="horizontal"
                        activeLabel={t('notifications.emailLabel')}
                        inactiveLabel={t('notifications.emailLabel')}
                      />
                      <FormFieldToggle
                        name="digitalReleaseNotifications.releaseApproved"
                        label={t(
                          'notifications.digitalRelease.approval.releaseApproved',
                        )}
                        layout="horizontal"
                        activeLabel={t('notifications.emailLabel')}
                        inactiveLabel={t('notifications.emailLabel')}
                      />
                    </div>
                  </ContentBox>

                  {/* Claiming */}
                  <ContentBox>
                    <h3 className="mb-lg text-headline-sm font-semibold">
                      {t('notifications.digitalRelease.claiming.title')}
                    </h3>
                    <div className="flex flex-col gap-md">
                      <FormFieldToggle
                        name="digitalReleaseNotifications.releaseClaimed"
                        label={t(
                          'notifications.digitalRelease.claiming.releaseClaimed',
                        )}
                        layout="horizontal"
                        activeLabel={t('notifications.emailLabel')}
                        inactiveLabel={t('notifications.emailLabel')}
                      />
                      <FormFieldToggle
                        name="digitalReleaseNotifications.releaseClaimingFailed"
                        label={t(
                          'notifications.digitalRelease.claiming.releaseClaimingFailed',
                        )}
                        layout="horizontal"
                        activeLabel={t('notifications.emailLabel')}
                        inactiveLabel={t('notifications.emailLabel')}
                      />
                    </div>
                  </ContentBox>
                </div>
              </ColumnLayout>
            </AccordionContentBox>
            <AccordionContentBox
              title={t('notifications.physicalRelease.title')}
              variant={{ level: 2 }}
              defaultOpen={false}
            >
              <div />
            </AccordionContentBox>
          </AccordionContentBox>

          <AccordionContentBox title={t('rolesAndPermissions.title')}>
            <ColumnLayout columns={2}>
              <div className="text-body-sm text-subtle">
                {t('rolesAndPermissions.roles')}
              </div>
              <div className="text-body-sm text-subtle">
                {t('rolesAndPermissions.labelGroups')}
              </div>
              <ContentBox className="col-span-2">
                <ColumnLayout columns={2}>
                  <div>{roles.join(', ')}</div>
                  <div>{labelGroups.join(', ')}</div>
                </ColumnLayout>
              </ContentBox>
            </ColumnLayout>
          </AccordionContentBox>
        </div>
      </Form>
    </Layout>
  );
}

MyProfilePage.displayName = 'MyProfilePage';
